import { useEffect, useCallback, useMemo, useRef } from 'react'
import { type TableDispatch, actions } from 'react-table-next'
import useDeepEffect from '../../../common/react/hooks/useDeepEffect'
import {
  type DashTablePaginationConfigurationProps,
  type DashTableManualPaginationProps,
  type DashTableBaseFiltersType,
  type DashTableState,
  type FetchDataSortBy,
} from './dashTable.types'
import { tableSortByToFetchDataSortBy } from './dashTable.helpers'

export type UseDashTableEffectsProps<TFilters> = Pick<
  DashTableManualPaginationProps<TFilters>,
  'manualPagination' | 'allowUnknownRecordCount' | 'fetchData' | 'filters'
> & {
  setPageCount: (nextPageCount: number) => void
  paginationConfiguration: DashTablePaginationConfigurationProps
  state: DashTableState
  dispatch: TableDispatch
}

export const useDashTableEffects = <TFilters extends DashTableBaseFiltersType>({
  setPageCount,
  manualPagination,
  allowUnknownRecordCount,
  fetchData,
  filters,
  paginationConfiguration: { totalRecordCount, firstVisibleRecordIndex },
  state: { pageIndex, pageSize, sortBy },
  dispatch,
}: UseDashTableEffectsProps<TFilters>) => {
  const isMounted = useRef(false)

  // If we are not in manualPagination mode, we don't need to update the page count.
  // If the totalRecordCount is undefined and we are not allowing unknown record counts,
  // then also won't manage the page count
  const manuallyManagedPageCount = useMemo(() => {
    if (!manualPagination) return false
    if (!allowUnknownRecordCount && totalRecordCount === undefined) return false

    return true
  }, [allowUnknownRecordCount, manualPagination, totalRecordCount])

  // If totalRecordCount is undefined, we manually set the max page count to
  // current + 1 - "there is at least one more page"
  useEffect(() => {
    if (manuallyManagedPageCount && totalRecordCount === undefined) {
      // One more page than the current page
      // Note that numPages is 1-indexed, while pageIndex is 0-indexed
      setPageCount(pageIndex + 2)
    }
  }, [manuallyManagedPageCount, pageIndex, setPageCount, totalRecordCount])

  // If totalRecordCount is defined, then we use it to calculate the max number of pages
  useEffect(() => {
    if (manuallyManagedPageCount && totalRecordCount !== undefined) {
      setPageCount(Math.ceil(totalRecordCount / pageSize))
    }
  }, [manuallyManagedPageCount, pageSize, setPageCount, totalRecordCount])

  // A helper utility for taking in filters, and fetching data
  const callFetchData = useCallback(
    (fetchDataFilters: TFilters, fetchDataSortBy?: FetchDataSortBy, signal?: AbortSignal) => {
      if (!manualPagination || !fetchData) return

      fetchData({
        limit: pageSize,
        offset: firstVisibleRecordIndex,
        filters: fetchDataFilters,
        sortBy: fetchDataSortBy,
        signal,
      })
    },
    // Note that the `fetchData` must be memoized or else this will re-trigger on every loop
    [fetchData, firstVisibleRecordIndex, manualPagination, pageSize]
  )

  // If the filters change, reset the page index if we are not on page 0
  // If we are already on page 0, trigger a search
  useDeepEffect(() => {
    // this code is meant to run when the filters or sortBy objects change
    // it does not need to be run on mount
    if (isMounted.current) {
      const abortController = new AbortController()

      if (pageIndex === 0) {
        callFetchData(
          filters as TFilters,
          tableSortByToFetchDataSortBy(sortBy),
          abortController.signal
        )
      } else {
        dispatch({ type: actions.resetPage })
      }
      return () => abortController.abort()
    } else {
      isMounted.current = true
    }
  }, [filters, sortBy])

  // Listen for changes in callFetchData which listens for changes on pagination
  // and use the new state to fetch our new data
  useEffect(() => {
    const abortController = new AbortController()
    callFetchData(filters as TFilters, tableSortByToFetchDataSortBy(sortBy), abortController.signal)
    return () => abortController.abort()
    // We exclude filters from this useEffect because we handle it as a seperate case
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callFetchData])
}
